<template>
  <div>
    <header class='report-page'>
      <h2>Network and Revenue</h2>
      <div style='display: flex; align-items: baseline;'>
        <report-controls v-model='spec' from-week1></report-controls>
        <a href='#' @click.stop='download' v-show='!loading'>Download as XLSX</a>
      </div>
    </header>
    <div class="wrapper">
      <div v-if="loading" class='wrapper'>
        Loading, please wait...
      </div>
      <div class='wrapper clm' v-else>
        <h2>Other Accounts</h2>
        <report-table id='report-table' :spec='spec' :headers='headers' :data='selectedPeriod' ref="table"
                      :dataIncludesTotals='dataIncludesTotals' :columnConfig='columnConfig' :totalRows='totalRows'
                      :sortInitial="sortInitial" can-breakdown :breakdowns="breakdowns" @breakdown="breakdown($event)"/>
      </div>
    </div>
  </div>
</template>

<script>
import ReportTable from '../components/ReportTable';
import ReportControls from '../components/ReportControls';
import axios from 'axios';
import XLSX from 'xlsx';
import Vue from 'vue';
import moment from 'moment';

export default {
  name: 'NetworkAndRevenue',
  components: {ReportControls, ReportTable},
  data() {
    return {
      sortInitial: {
        direction: 'DESC',
        index: 0,
      },
      selectedPeriod: [],
      selectedPeriodPW: [],
      loading: false,
      spec: {},
      headers: [
        'YrWk',
        'General Revenue (£)',
        'X-Dock Revenue (£)',
        'Network Revenue (£)',
        'Network Margin (£)',
        'Network Margin (%)',
        'Network Spaces',
        'Network Sales Price per Pallet (£)',
        'Network Margin per Pallets (£)',
        'Network - Premium Pallets (%)',
        'Network - Economy Pallets (%)',
        'PWAYS Revenue (£)',
        'PWAYS Spaces',
        'PWAYS Rev./Spaces (£)',
      ],
      data: [],
      dataIncludesTotals: false,
      columnConfig: [
        {
          // YrWk
          format: (x) => x.toString(),
        },
        {
          // General Revenue
          format: 'currency',
          decimalPlaces: 0,
          aggregate: 'total',
        },
        {
          // X-Dock Revenue
          format: 'currency',
          decimalPlaces: 0,
          aggregate: 'total',
        },
        {
          // Network Revenue
          format: 'currency',
          decimalPlaces: 0,
          aggregate: 'total',
        },
        {
          // Network Margin
          format: 'currency',
          decimalPlaces: 0,
          aggregate: 'total',
        },
        {
          // Network Margin %
          format: 'percent',
          decimalPlaces: 1,
          aggregate: (a, b) => {
            // Network Margin / Network Revenue
            return b[3] / (b[1] + b[2]) * 100;
          },

        },
        {
          // Network Spaces
          format: 'number',
        },
        {
          // Network Sales Price per Pallet
          format: 'currency',
          decimalPlaces: 2,
          aggregate: (a, b) => {
            // Network Revenue / Network Spaces
            return (b[1] + b[2]) / b[5];
          },
        },
        {
          // Network Margin per Pallet
          format: 'currency',
          decimalPlaces: 2,
          aggregate: (a, b) => {
            // Network Margin / Network Spaces
            return b[3] / b[5];
          },
        },
        {
          // Network Premium Pallets %
          format: 'percent',
          decimalPlaces: 1,
          aggregate: 'mean',
        },
        {
          // Network Economy Pallets %
          format: 'percent',
          decimalPlaces: 1,
          aggregate: 'mean',
        },
        {
          // PWAYS Revenue
          format: 'currency',
          decimalPlaces: 0,
          aggregate: 'total',
        },
        {
          // PWAYS Spaces
          format: 'number',
          aggregate: 'total',
        },
        {
          // PWays Rev per Space
          format: 'currency',
          decimalPlaces: 2,
          aggregate: (a, b) => {
            // PWAYS Revenue / PWAYS Spaces
            return b[10] / b[11];
          },
        },
      ],
      aggregates: {
        'Margin (%)'(vals) {
          return vals[4] / vals[1] * 100;
        },
      },
      breakdowns: {},
      totalRows: false,
    };
  },
  computed: {
    dataUrl() {
      const {depot, from, to} = this.spec;
      return this.buildDataUrl(depot, from, to);
    },
  },
  methods: {
    download() {
      const wb = XLSX.utils.book_new();
      const selectedPeriodSheet = XLSX.utils.table_to_sheet((this.$refs.table).$el);
      XLSX.utils.book_append_sheet(wb, selectedPeriodSheet, 'Selected Period');
      const {depot, from, to} = this.spec;
      XLSX.writeFile(wb, `Network and Revenue for Depot ${depot} -
        ${from.year}${from.week} to ${to.year}${to.week}.xlsx`);
    },
    buildDataUrl(depot, from, to) {
      const BASE_URL = (process.env.NODE_ENV === 'production') ? '/api' : '//localhost:8000';
      return `${BASE_URL}/reports/network-and-revenue/${depot}/${from.year}/${from.week}/${to.year}/${to.week}/`;
    },
    async breakdown({key, sort}) {
      if (key in this.breakdowns) {
        this.$delete(this.breakdowns, key);
        return;
      }
      const yearWeek = {
        year: '20' + parseInt(key.slice(2, 4), 10),
        week: parseInt(key.slice(5, 7), 10),
      };
      const url = this.buildDataUrl(this.spec.depot, yearWeek, yearWeek);
      const response = await axios.get(url, {
        params: {
          account_codes: this.spec.accountCodes.flatMap((a) => a.codes),
          daily: true,
        },
      });
      const days = response.data.results.selectedPeriod;
      if (sort) {
        days.sort((a, b) => {
          if (typeof (a[sort.index]) === 'string') {
            if (sort.direction === 'DESC') {
              return b[sort.index].localeCompare(a[sort.index]);
            } else {
              return a[sort.index].localeCompare(b[sort.index]);
            }
          } else {
            if (sort.direction === 'DESC') {
              return b[sort.index] - a[sort.index];
            } else {
              return a[sort.index] - b[sort.index];
            }
          }
        });
      }
      this.$set(this.breakdowns, key, days.map((r) => {
        return [
          moment(r[0]).format('ddd DD MMM'),
          ...r.slice(1),
        ];
      }));
    },
  },
  watch: {
    async spec() {
      this.loading = true;
      const response = await axios.get(this.dataUrl, {
        params: {
          account_codes: this.spec.accountCodes.flatMap((a) => a.codes),
        },
      });
      this.selectedPeriod = response.data.results.selectedPeriod;
      this.selectedPeriodPW = response.data.results.selectedPeriodPW;
      this.loading = false;
    },
  },
};

</script>

<style scoped>
.wrapper {
  display: flex;
  justify-content: center;
  align-items: flex-start;
}

#report-table {
  width: 1100px;
}

.clm {
  flex-direction: column;
}

h2 {
  margin-bottom: 1rem;
}


</style>
